/**
 * Tiny fetch wrapper for talking to the SLS Short API. Handles CSRF token
 * injection, JSON encoding, and consistent error handling.
 */

import type { Organization, User } from '@shared/types';

export interface ApiError extends Error {
  status: number;
  code?: string;
}

let csrfToken: string | null = null;
export function setCsrfToken(token: string | null) {
  csrfToken = token;
}
export function getCsrfToken(): string | null {
  if (csrfToken) return csrfToken;
  // Read from cookie as a fallback.
  const m = document.cookie.match(/(?:^|; )sls_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

export interface ApiResult<T> {
  data: T;
}

async function request<T>(
  method: string,
  url: string,
  body?: unknown,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (body !== undefined && !(body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  if (method !== 'GET' && method !== 'HEAD') {
    const csrf = getCsrfToken();
    if (csrf) headers.set('X-Sls-Csrf', csrf);
  }
  const res = await fetch(url, {
    method,
    headers,
    credentials: 'include',
    body:
      body === undefined
        ? undefined
        : body instanceof FormData
          ? body
          : JSON.stringify(body),
  });

  let payload: unknown = null;
  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    payload = await res.json().catch(() => null);
  } else if (res.status !== 204) {
    payload = await res.text().catch(() => null);
  }

  if (!res.ok) {
    const errBody =
      payload && typeof payload === 'object' && 'error' in payload
        ? (payload as { error?: { code?: string; message?: string } }).error
        : null;
    const message =
      errBody?.message ||
      (typeof payload === 'string' ? payload : '') ||
      `Request failed: ${res.status}`;
    const error = new Error(message) as ApiError;
    error.status = res.status;
    if (errBody?.code) error.code = errBody.code;
    throw error;
  }

  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as ApiResult<T>).data;
  }
  return payload as T;
}

export const api = {
  get: <T>(url: string) => request<T>('GET', url),
  post: <T>(url: string, body?: unknown) => request<T>('POST', url, body),
  put: <T>(url: string, body?: unknown) => request<T>('PUT', url, body),
  patch: <T>(url: string, body?: unknown) => request<T>('PATCH', url, body),
  del: <T>(url: string) => request<T>('DELETE', url),
};

// Auth-specific helpers
export interface MeResponse {
  user: User | null;
  organization: Organization | null;
  csrfToken: string;
}

export async function fetchMe(): Promise<MeResponse> {
  const data = await api.get<MeResponse>('/api/auth/me');
  if (data.csrfToken) setCsrfToken(data.csrfToken);
  return data;
}

export async function login(email: string, password: string): Promise<MeResponse> {
  const data = await api.post<MeResponse>('/api/auth/login', { email, password });
  if (data.csrfToken) setCsrfToken(data.csrfToken);
  return data;
}

export async function logout(): Promise<void> {
  await api.post('/api/auth/logout');
}
